import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

class FileCacheManager extends CacheManager {
	constructor() {
		super('FileCache', 'files', {
			keyPath: 'id',
			maxAge: 7 * 24 * 60 * 60 * 1000,
			logCategory: 'File',
		});
	}

	async putFile(id, blob, mimeType) {
		if (!this.db) await this.initDB();

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readwrite');
			const store = transaction.objectStore(this.storeName);

			const fileData = {
				id: id,
				blob: blob,
				mimeType: mimeType,
				timestamp: Date.now(),
			};

			const request = store.put(fileData);
			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	async getFile(id) {
		if (!this.db) await this.initDB();

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readonly');
			const store = transaction.objectStore(this.storeName);

			const request = store.get(id);
			request.onsuccess = () => {
				const result = request.result;
				resolve(result ? result.blob : null);
			};
			request.onerror = () => reject(request.error);
		});
	}
}

const fileCache = new FileCacheManager();

/**
 * Bounded LRU cache of blob object URLs. The previous implementation was an
 * unbounded Map that never revoked its object URLs (revocation only lived in the
 * never-called clearFileCache() full-clear branch), so every distinct file id ever
 * viewed pinned its Blob in memory for the whole session — a RAM plateau that only
 * ratcheted up. This caps the number of live object URLs and revokes on eviction so
 * memory is bounded. Null entries (offline/error misses) are kept too so we don't
 * re-hit the network on every render; they carry no memory and evict like any other.
 */
class LruUrlCache {
	constructor(maxEntries) {
		this.maxEntries = maxEntries;
		this.map = new Map();
	}

	has(key) {
		return this.map.has(key);
	}

	get(key) {
		if (!this.map.has(key)) return undefined;
		// refresh recency
		const value = this.map.get(key);
		this.map.delete(key);
		this.map.set(key, value);
		return value;
	}

	set(key, value) {
		if (this.map.has(key)) this.#revoke(this.map.get(key));
		this.map.set(key, value);
		while (this.map.size > this.maxEntries) {
			const oldestKey = this.map.keys().next().value;
			this.#revoke(this.map.get(oldestKey));
			this.map.delete(oldestKey);
		}
	}

	delete(key) {
		if (!this.map.has(key)) return;
		this.#revoke(this.map.get(key));
		this.map.delete(key);
	}

	clear() {
		for (const value of this.map.values()) this.#revoke(value);
		this.map.clear();
	}

	get size() {
		return this.map.size;
	}

	#revoke(value) {
		if (value && typeof value === 'string' && value.startsWith('blob:')) {
			URL.revokeObjectURL(value);
		}
	}
}

// Full-resolution object URLs (rarely populated now — account logos go through the
// downscaled thumbnail path below; kept for any full-res consumer + the thumb fallback).
const fullUrlCache = new LruUrlCache(120);
// Downscaled account-image thumbnails (the common path — tiny map/detail logos).
const thumbUrlCache = new LruUrlCache(200);

const ACCOUNT_THUMB_MAX_PX = 96;

function isValidFileId(fileId) {
	return fileId && fileId !== '00000000-0000-0000-0000-000000000000' && fileId.length === 36;
}

/**
 * Returns the file Blob (from IndexedDB, else network with write-through cache),
 * or null on offline miss / error. Never throws and never creates an object URL —
 * so it is safe to use for warming the on-disk cache (see prefetchFile) without
 * pinning anything in RAM.
 */
async function fetchBlob(fileId) {
	const cachedBlob = await fileCache.getFile(fileId);
	if (cachedBlob) return cachedBlob;

	if (iframeDataBridge.isOffline()) {
		log('info', 'File', `📴 Offline: file ${fileId} not in cache`);
		return null;
	}

	const apiUrl = getApiUrl(`Core/Files/${fileId}`);
	const response = await authenticatedFetch(apiUrl, {}, 'token');

	if (!response.ok) return null;

	const blob = await response.blob();
	const mimeType = response.headers.get('content-type') || 'application/octet-stream';
	await fileCache.putFile(fileId, blob, mimeType);
	return blob;
}

/**
 * Downscale an image Blob to a thumbnail no larger than maxPx on its longest side.
 * Preserves transparency (PNG). Rejects on any decode/encode failure so callers can
 * fall back to the full-resolution URL.
 */
function downscaleImageBlob(blob, maxPx) {
	return new Promise((resolve, reject) => {
		const srcUrl = URL.createObjectURL(blob);
		const img = new Image();

		img.onload = () => {
			try {
				const longest = Math.max(img.naturalWidth, img.naturalHeight);
				if (!longest) {
					URL.revokeObjectURL(srcUrl);
					reject(new Error('Zero-dimension image'));
					return;
				}
				const scale = longest > maxPx ? maxPx / longest : 1;
				const w = Math.max(1, Math.round(img.naturalWidth * scale));
				const h = Math.max(1, Math.round(img.naturalHeight * scale));

				const canvas = document.createElement('canvas');
				canvas.width = w;
				canvas.height = h;
				canvas.getContext('2d').drawImage(img, 0, 0, w, h);

				canvas.toBlob((thumbBlob) => {
					URL.revokeObjectURL(srcUrl);
					if (thumbBlob) resolve(thumbBlob);
					else reject(new Error('toBlob returned null'));
				}, 'image/png');
			} catch (error) {
				URL.revokeObjectURL(srcUrl);
				reject(error);
			}
		};

		img.onerror = () => {
			URL.revokeObjectURL(srcUrl);
			reject(new Error('Image decode failed'));
		};

		img.src = srcUrl;
	});
}

/**
 * Full-resolution object URL for a file. Bounded/LRU-cached and revoked on eviction.
 * Most account-logo callers should use getAccountImageUrl (downscaled) instead.
 */
export async function getFileUrl(fileId) {
	if (!isValidFileId(fileId)) return null;

	if (fullUrlCache.has(fileId)) return fullUrlCache.get(fileId);

	try {
		const blob = await fetchBlob(fileId);
		const objectUrl = blob ? URL.createObjectURL(blob) : null;
		fullUrlCache.set(fileId, objectUrl);
		return objectUrl;
	} catch (error) {
		console.error('Error loading file:', error);
		fullUrlCache.set(fileId, null);
		return null;
	}
}

export async function getFileBlob(fileId) {
	try {
		const cachedBlob = await fileCache.getFile(fileId);
		if (cachedBlob) {
			return cachedBlob;
		}

		if (iframeDataBridge.isOffline()) {
			throw new Error(`Offline: File ${fileId} not available in cache`);
		}

		const apiUrl = getApiUrl(`Core/Files/${fileId}`);
		const response = await authenticatedFetch(apiUrl, {}, 'token');

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const blob = await response.blob();
		const mimeType = response.headers.get('content-type') || 'application/octet-stream';

		await fileCache.putFile(fileId, blob, mimeType);

		return blob;
	} catch (error) {
		log('error', 'File', `Error loading file: ${error.message}`);
		throw error;
	}
}

export async function getFileAsBase64(fileId) {
	try {
		const blob = await getFileBlob(fileId);
		if (!blob) return null;

		return await convertBlobToBase64(blob);
	} catch (error) {
		console.error('Error converting file to base64:', error);
		return null;
	}
}

function convertBlobToBase64(blob) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => resolve(reader.result);
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
}

/**
 * Downscaled object URL for an account logo (tiny map markers / detail panels).
 * Holds a ~96px thumbnail in RAM instead of the full-resolution original, and
 * revokes on LRU eviction. Falls back to the full-resolution URL if downscaling
 * fails, so callers always get a usable URL when the file exists.
 */
export async function getAccountImageUrl(fileId, maxPx = ACCOUNT_THUMB_MAX_PX) {
	if (!isValidFileId(fileId)) return null;

	if (thumbUrlCache.has(fileId)) return thumbUrlCache.get(fileId);

	try {
		const blob = await fetchBlob(fileId);
		if (!blob) {
			thumbUrlCache.set(fileId, null);
			return null;
		}

		try {
			const thumbBlob = await downscaleImageBlob(blob, maxPx);
			const objectUrl = URL.createObjectURL(thumbBlob);
			thumbUrlCache.set(fileId, objectUrl);
			return objectUrl;
		} catch (downscaleError) {
			log('warn', 'File', `Thumbnail downscale failed for ${fileId}, using full image: ${downscaleError.message}`);
			return await getFileUrl(fileId);
		}
	} catch (error) {
		console.error('Error loading account image:', error);
		thumbUrlCache.set(fileId, null);
		return null;
	}
}

/**
 * Ensure a file's Blob is in the on-disk (IndexedDB) cache for offline use WITHOUT
 * creating an object URL — i.e. without pinning it in RAM. Use this for bulk
 * preloading (sync) where nothing is displayed yet.
 */
export async function prefetchFile(fileId) {
	if (!isValidFileId(fileId)) return;
	try {
		await fetchBlob(fileId);
	} catch (error) {
		log('warn', 'File', `Prefetch failed for ${fileId}: ${error.message}`);
	}
}

/**
 * Revoke and drop all in-memory object URLs WITHOUT clearing IndexedDB — frees RAM
 * while keeping the on-disk cache intact for offline. Safe to call on memory pressure.
 */
export function releaseInMemoryFileUrls() {
	const freed = fullUrlCache.size + thumbUrlCache.size;
	fullUrlCache.clear();
	thumbUrlCache.clear();
	log('info', 'File', `🧹 Released ${freed} in-memory file object URLs`);
}

export async function clearFileCache(fileId = null) {
	if (fileId) {
		await fileCache.delete(fileId);
		fullUrlCache.delete(fileId);
		thumbUrlCache.delete(fileId);
	} else {
		await fileCache.clearAll();
		releaseInMemoryFileUrls();
	}
}

export async function cleanOldCacheEntries(maxAgeInDays = 7) {
	await fileCache.cleanOld(maxAgeInDays);
}

export async function getCacheStats() {
	const stats = await fileCache.getStats();
	return {
		...stats,
		inMemoryUrls: fullUrlCache.size + thumbUrlCache.size,
	};
}

cleanOldCacheEntries();
